import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Dialog } from "@/components/ui/dialog";
import { useActiveCalls, useHangupCall, useTransferCall, useOriginateCall, useExtensions } from "@/hooks/use-calls";
import { useContexts } from "@/hooks/use-contexts";
import { useEndpoints } from "@/hooks/use-endpoints";
import type { OriginateCallRequest } from "@/types";
import { PhoneOff, ArrowRightLeft, PhoneCall } from "lucide-react";
import { toast } from "sonner";

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "-";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatMs(ms: number | null): string {
  if (ms === null) return "-";
  return `${ms.toFixed(1)} ms`;
}

export default function ActiveCallsPage() {
  const { data: calls, isLoading } = useActiveCalls();
  const hangup = useHangupCall();
  const transfer = useTransferCall();
  const originate = useOriginateCall();
  const { data: extensions } = useExtensions();
  const { data: contexts } = useContexts();
  const { data: endpoints } = useEndpoints();
  const [transferChannel, setTransferChannel] = useState<string | null>(null);
  const [destination, setDestination] = useState("");
  const [showNewCall, setShowNewCall] = useState(false);
  const [callerType, setCallerType] = useState<"extension" | "endpoint">("extension");
  const [callerContext, setCallerContext] = useState("");
  const [callerExt, setCallerExt] = useState("");
  const [callerEndpoint, setCallerEndpoint] = useState("");
  const [destType, setDestType] = useState<"extension" | "trunk" | "sip_uri">("extension");
  const [destExt, setDestExt] = useState("");
  const [destTrunk, setDestTrunk] = useState("");
  const [destNumber, setDestNumber] = useState("");
  const [destUri, setDestUri] = useState("");
  const [callerName, setCallerName] = useState("");
  const [callTimeout, setCallTimeout] = useState(30);

  const sortedExtensions = useMemo(() =>
    (extensions ?? []).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [extensions],
  );

  const handleHangup = (channel: string) => {
    hangup.mutate(channel, {
      onSuccess: () => toast.success("Call terminated"),
      onError: () => toast.error("Failed to hang up call"),
    });
  };

  const openTransfer = (channel: string) => {
    setTransferChannel(channel);
    setDestination("");
  };

  const handleTransfer = () => {
    if (!transferChannel) return;
    transfer.mutate(
      { channel: transferChannel, destination },
      {
        onSuccess: () => {
          toast.success("Call transferred");
          setTransferChannel(null);
        },
        onError: () => toast.error("Failed to transfer call"),
      }
    );
  };

  const handleNewCall = () => {
    const body: OriginateCallRequest = {
      caller_type: callerType,
      dest_type: destType,
      caller_name: callerName || undefined,
      timeout: callTimeout,
    };

    if (callerType === "extension") {
      body.caller_ext = callerExt;
      body.caller_context = callerContext || undefined;
    } else {
      body.caller_endpoint = callerEndpoint;
    }

    if (destType === "extension") {
      body.dest_ext = destExt;
    } else if (destType === "trunk") {
      body.dest_trunk = destTrunk;
      body.dest_number = destNumber;
    } else {
      body.dest_uri = destUri;
    }

    originate.mutate(body, {
      onSuccess: () => {
        toast.success("Originating call");
        setShowNewCall(false);
        setCallerExt("");
        setCallerEndpoint("");
        setDestExt("");
        setDestNumber("");
        setDestUri("");
        setCallerName("");
        setCallTimeout(30);
      },
      onError: (err) => toast.error(`Failed: ${err.message}`),
    });
  };

  const canSubmit = (() => {
    if (callerType === "extension" && !callerExt.trim()) return false;
    if (callerType === "endpoint" && !callerEndpoint) return false;
    if (destType === "extension" && !destExt.trim()) return false;
    if (destType === "trunk" && (!destTrunk || !destNumber.trim())) return false;
    if (destType === "sip_uri" && !destUri.trim()) return false;
    return true;
  })();

  const colSpan = 12;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Active Calls</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {calls?.length ?? 0} active call{(calls?.length ?? 0) !== 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={() => setShowNewCall(true)} size="sm" className="gap-1.5">
          <PhoneCall size={14} />
          New Call
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th className="px-3 py-2 font-medium">Channel</th>
              <th className="px-3 py-2 font-medium">Call ID</th>
              <th className="px-3 py-2 font-medium">Caller</th>
              <th className="px-3 py-2 font-medium">Connected</th>
              <th className="px-3 py-2 font-medium">State</th>
              <th className="px-3 py-2 font-medium">Duration</th>
              <th className="px-3 py-2 font-medium">Jitter RX</th>
              <th className="px-3 py-2 font-medium">Jitter TX</th>
              <th className="px-3 py-2 font-medium">Lost PKT</th>
              <th className="px-3 py-2 font-medium">RTT</th>
              <th className="px-3 py-2 font-medium w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={colSpan} className="px-3 py-8 text-center text-muted-foreground">Loading...</td></tr>
            )}
            {!isLoading && (!calls || calls.length === 0) && (
              <tr><td colSpan={colSpan} className="px-3 py-8 text-center text-muted-foreground">No active calls</td></tr>
            )}
            {calls?.map((call) => (
              <tr key={call.unique_id} className="border-b border-border hover:bg-muted/30">
                <td className="px-3 py-2 font-mono text-xs max-w-[200px] truncate">{call.channel}</td>
                <td className="px-3 py-2 font-mono text-xs max-w-[120px] truncate" title={call.linked_id ?? ""}>
                  {call.linked_id || "-"}
                </td>
                <td className="px-3 py-2">
                  <div>{call.caller_id_num || "-"}</div>
                  {call.caller_id_name && <div className="text-xs text-muted-foreground">{call.caller_id_name}</div>}
                </td>
                <td className="px-3 py-2">{call.connected_line_num || "-"}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    call.state === "Up" ? "bg-green-100 text-green-800" :
                    call.state === "Ringing" ? "bg-yellow-100 text-yellow-800" :
                    "bg-gray-100 text-gray-800"
                  }`}>
                    {call.state || "-"}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono">{formatDuration(call.duration_seconds)}</td>
                <td className="px-3 py-2 font-mono text-xs">{formatMs(call.jitter_rx)}</td>
                <td className="px-3 py-2 font-mono text-xs">{formatMs(call.jitter_tx)}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {call.packets_lost_rx !== null ? `${call.packets_lost_rx} / ${call.packets_lost_tx ?? "-"}` : "-"}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{formatMs(call.rtt_seconds ? call.rtt_seconds * 1000 : null)}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleHangup(call.channel)}
                      disabled={hangup.isPending}
                      className="h-7 px-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Hang up"
                    >
                      <PhoneOff size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openTransfer(call.channel)}
                      className="h-7 px-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Transfer"
                    >
                      <ArrowRightLeft size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={transferChannel !== null}
        onClose={() => setTransferChannel(null)}
        title="Transfer Call"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select an extension or enter extension@host or sip:uri to transfer
          </p>
          {sortedExtensions.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Configured Extensions</label>
              <Select
                value=""
                onChange={(e) => setDestination(e.target.value)}
              >
                <option value="">-- select extension --</option>
                {sortedExtensions.map((ext) => (
                  <option key={ext} value={ext}>{ext}</option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Custom Destination</label>
            <Input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="1001@endpoint or sip:user@provider.com"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setTransferChannel(null)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={transfer.isPending || !destination.trim()}>
              Transfer
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={showNewCall}
        onClose={() => setShowNewCall(false)}
        title="New Call"
      >
        <div className="space-y-4">
          <fieldset>
            <legend className="text-sm font-medium mb-2">Caller Source</legend>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="callerType"
                  value="extension"
                  checked={callerType === "extension"}
                  onChange={() => setCallerType("extension")}
                />
                Internal extension
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="callerType"
                  value="endpoint"
                  checked={callerType === "endpoint"}
                  onChange={() => setCallerType("endpoint")}
                />
                PJSIP endpoint
              </label>
            </div>
            {callerType === "extension" ? (
              <div className="flex gap-2">
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Context</label>
                  <Select
                    value={callerContext}
                    onChange={(e) => setCallerContext(e.target.value)}
                  >
                    <option value="">default</option>
                    {(contexts ?? []).map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Extension</label>
                  <Input
                    value={callerExt}
                    onChange={(e) => setCallerExt(e.target.value)}
                    placeholder="1001"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Endpoint</label>
                <Select
                  value={callerEndpoint}
                  onChange={(e) => setCallerEndpoint(e.target.value)}
                >
                  <option value="">-- select endpoint --</option>
                  {(endpoints ?? []).map((ep) => (
                    <option key={ep.id} value={ep.name}>{ep.name}</option>
                  ))}
                </Select>
              </div>
            )}
          </fieldset>

          <div className="border-t border-border" />

          <fieldset>
            <legend className="text-sm font-medium mb-2">Destination</legend>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="destType"
                  value="extension"
                  checked={destType === "extension"}
                  onChange={() => setDestType("extension")}
                />
                Internal extension
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="destType"
                  value="trunk"
                  checked={destType === "trunk"}
                  onChange={() => setDestType("trunk")}
                />
                External via trunk
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="destType"
                  value="sip_uri"
                  checked={destType === "sip_uri"}
                  onChange={() => setDestType("sip_uri")}
                />
                Direct SIP URI
              </label>
            </div>
            {destType === "extension" ? (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Extension number</label>
                <Input
                  value={destExt}
                  onChange={(e) => setDestExt(e.target.value)}
                  placeholder="1002"
                />
              </div>
            ) : destType === "trunk" ? (
              <div className="flex gap-2">
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Trunk</label>
                  <Select
                    value={destTrunk}
                    onChange={(e) => setDestTrunk(e.target.value)}
                  >
                    <option value="">-- select trunk --</option>
                    {(endpoints ?? []).map((ep) => (
                      <option key={ep.id} value={ep.name}>{ep.name}</option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs text-muted-foreground">Number</label>
                  <Input
                    value={destNumber}
                    onChange={(e) => setDestNumber(e.target.value)}
                    placeholder="+1234567890"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">SIP URI</label>
                <Input
                  value={destUri}
                  onChange={(e) => setDestUri(e.target.value)}
                  placeholder="sip:user@provider.com"
                />
              </div>
            )}
          </fieldset>

          <div className="border-t border-border" />

          <div className="flex gap-2">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs text-muted-foreground">Caller Name (optional)</label>
              <Input
                value={callerName}
                onChange={(e) => setCallerName(e.target.value)}
                placeholder="Alice"
              />
            </div>
            <div className="w-24 space-y-1.5">
              <label className="text-xs text-muted-foreground">Timeout (s)</label>
              <Input
                type="number"
                value={String(callTimeout)}
                onChange={(e) => setCallTimeout(Number(e.target.value) || 30)}
                min={1}
                max={120}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setShowNewCall(false)}>Cancel</Button>
            <Button onClick={handleNewCall} disabled={originate.isPending || !canSubmit}>
              Call
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}